import { memo, ReactNode, useCallback } from "react";
import * as React from "react";

import { ShareArrowUp } from "@hl/base-components/lib/assets/icons.generated";
import { SECONDARY_COLOR } from "@hl/base-components/lib/theme/button";
import {
  FEATURE_FLAGS,
  useFeatureFlags,
} from "@hl/shared-features/lib/features/auth/hooks";
import { useModalStack } from "@hl/shared-features/lib/features/modal";
import { Button, Stack, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { ReactComponent as BrowserIcon } from "assets/icons/browser.svg";
import { ReactComponent as EyeIcon } from "assets/icons/eye-fill.svg";
import { ReactComponent as PauseIcon } from "assets/icons/pause-rounded.svg";
import { ReactComponent as PlayIcon } from "assets/icons/play-outlined.svg";

import { _CollectionType } from "../../../../../apollo/graphql.generated";
import { ModalType, updateModalVar } from "../../../../layout/Modal/modal";
import EmbedSaleModal from "../modals/EmbedSaleModal";
import { SaleAvailabilityModalData } from "../modals/SaleAvailabilityModal";
import ShareSaleModal from "../modals/ShareSaleModal";
import { MintVector, SaleAvailability, SaleStatus } from "../types";

interface SaleButtonConfig {
  icon: ReactNode;
  text: string;
}

const saleButtonConfigMap: { [K in SaleAvailability]: SaleButtonConfig } = {
  [SaleAvailability.RESTART]: {
    icon: <PlayIcon />,
    text: "Restart mint",
  },
  [SaleAvailability.PAUSE]: {
    icon: <PauseIcon />,
    text: "Pause mint",
  },
};

const saleButtonStatusConfigMap: {
  [K in SaleStatus]?: SaleAvailability;
} = {
  [SaleStatus.ACTIVE]: SaleAvailability.PAUSE,
  [SaleStatus.ENDED]: SaleAvailability.RESTART,
  [SaleStatus.PAUSED]: SaleAvailability.RESTART,
};

const SaleButtonGroup = ({
  status,
  mintVector,
  mintUrl,
  collection,
}: {
  status: SaleStatus;
  mintVector: MintVector;
  collection: {
    id: string;
    collectionType: _CollectionType;
  };
  mintUrl: string;
}) => {
  // const { trackEvent, EventType } = useAnalytics();
  const saleAvailability = saleButtonStatusConfigMap[status];
  const showEmbed = useFeatureFlags(FEATURE_FLAGS.EMBEDS_SHOW_MODAL);

  const [openedEmbedModal, embedModalActions] = useDisclosure(false);

  const availabilityOnClick = useCallback(
    () =>
      updateModalVar<SaleAvailabilityModalData>({
        showModal: ModalType.SALE_AVAILABILITY,
        data: {
          id: mintVector.id,
          availability: saleAvailability!,
          startDate: new Date(mintVector.startDate),
          endDate: mintVector.endDate
            ? new Date(mintVector.endDate)
            : undefined,
          collectionId: mintVector.collectionId,
          collectionType: collection.collectionType,
          isDirectMint: mintVector.isDirectMint ?? false,
        },
      }),
    [mintVector, saleAvailability, collection]
  );

  const { pushModal } = useModalStack();

  const shareSaleOnClick = useCallback(
    () =>
      pushModal(
        <ShareSaleModal
          data={{
            entity: "mint",
            mintUrl,
            chainId: parseInt(mintVector.chainId),
          }}
        />,
        {
          size: "lg",
          title: <Text>Share mint page</Text>,
        }
      ),
    [mintUrl]
  );

  return (
    <Stack p="md">
      {saleAvailability === SaleAvailability.RESTART && (
        <Button
          fullWidth
          color={SECONDARY_COLOR}
          leftIcon={saleButtonConfigMap[saleAvailability].icon}
          onClick={availabilityOnClick}
        >
          {saleButtonConfigMap[saleAvailability].text}
        </Button>
      )}
      <Button
        fullWidth
        component="a"
        color={SECONDARY_COLOR}
        leftIcon={<EyeIcon />}
        target="_blank"
        href={mintUrl}
      >
        View mint page
      </Button>
      <Button
        fullWidth
        color={SECONDARY_COLOR}
        leftIcon={<ShareArrowUp />}
        onClick={shareSaleOnClick}
      >
        Share mint page
      </Button>
      {showEmbed && (
        <>
          <Button
            fullWidth
            color={SECONDARY_COLOR}
            leftIcon={<BrowserIcon />}
            onClick={embedModalActions.open}
          >
            Embed mint page
          </Button>
          <EmbedSaleModal
            opened={openedEmbedModal}
            onClose={embedModalActions.close}
            collectionId={mintVector.collectionId}
            entity={"mint"}
          />
        </>
      )}
    </Stack>
  );
};

export default memo(SaleButtonGroup);
